import { Alert, Button, Drawer, Form, Input, InputNumber, Space, Spin, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import AccountReadinessStatus from './AccountReadinessStatus';
import { getAccountPlatformLabel, getAuthStateColor } from '../utils/accountReadiness';

const TimedPublishScheduleDrawer = ({
  open,
  publishTimeForm,
  selectedAccounts,
  selectedTargetAccounts,
  parsingSchedule,
  scheduleParseResult,
  taskCreateBlock,
  taskQuotaTooltip,
  handleBack,
  handleCancel,
  handleParseSchedule,
  handleCreateTask,
}) => (
  <Drawer
    title="创建发布任务-选择发布时间"
    placement="right"
    width={800}
    onClose={handleCancel}
    open={open}
    mask
    maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
    footer={(
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>返回</Button>
        <Space>
          <Button onClick={handleCancel}>取消</Button>
          <Tooltip title={taskCreateBlock.blocked ? taskQuotaTooltip : null}>
            <span>
              <Button
                type="primary"
                onClick={handleCreateTask}
                disabled={taskCreateBlock.blocked || !scheduleParseResult || !scheduleParseResult.success}
              >
                创建任务
              </Button>
            </span>
          </Tooltip>
        </Space>
      </div>
    )}
  >
    <Form form={publishTimeForm} layout="vertical" style={{ padding: '16px 0' }}>
      {selectedAccounts.length > 0 && (
        <div style={{
          padding: '16px',
          background: '#f5f5f5',
          borderRadius: 4,
          marginBottom: 24,
        }}
        >
          <div style={{ marginBottom: 12, fontSize: 14, color: '#333' }}>
            本次任务将使用以下账号：
          </div>
          {selectedAccounts.map((account) => (
            <div
              key={account.account_id}
              style={{ marginBottom: 8, padding: 12, background: '#fff', borderRadius: 4 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Tag color="blue">{getAccountPlatformLabel(account)}</Tag>
                <span style={{ marginRight: 8 }}>账号ID: {account.account_id}</span>
                <span>账号名: {account.account_name}</span>
              </div>
              <AccountReadinessStatus account={account} />
            </div>
          ))}
        </div>
      )}

      <Form.Item
        label="定时任务名称"
        name="task_name"
        rules={[{ required: true, message: '请输入定时任务名称' }]}
      >
        <Input placeholder="请输入定时任务名称" />
      </Form.Item>

      <Form.Item
        label={selectedAccounts?.length === 1 ? '本次最多发布条数' : '每账号最多发布条数'}
        name="max_items_per_account_per_run"
        initialValue={1}
        rules={[{ required: true, message: '请输入条数' }]}
      >
        <InputNumber min={1} max={50} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        label="任务并发数"
        name="max_concurrency"
        initialValue={2}
        rules={[{ required: true, message: '请输入并发数' }]}
      >
        <InputNumber min={1} max={10} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        label="发布时间"
        name="schedule_text"
        rules={[{ required: true, message: '请输入发布时间文本' }]}
      >
        <Input.TextArea
          rows={4}
          placeholder={[
            '示例（Cron）：每周一到周五 9:00 执行',
            '示例（一次）：2026-01-15 14:30 执行一次',
            '示例（间隔）：从现在开始每 2 小时执行一次，持续 3 天',
          ].join('\n')}
        />
      </Form.Item>

      <Form.Item>
        <Button type="primary" onClick={handleParseSchedule} loading={parsingSchedule}>
          文本解析
        </Button>
      </Form.Item>

      {selectedTargetAccounts.length > 0 ? (
        <div style={{
          padding: 12,
          marginBottom: 16,
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          background: '#fafafa',
        }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>目标账号状态</div>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {selectedTargetAccounts.map((account) => (
              <div
                key={account.account_id}
                style={{ padding: 12, borderRadius: 6, background: '#fff' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Tag color="blue">{getAccountPlatformLabel(account)}</Tag>
                  <span style={{ marginRight: 8 }}>账号ID: {account.account_id}</span>
                  <span>账号名: {account.account_name || '-'}</span>
                  {account?.login_readiness?.auth_state ? (
                    <Tag color={getAuthStateColor(account.login_readiness.auth_state)}>
                      {account.login_readiness.auth_state}
                    </Tag>
                  ) : null}
                </div>
                <AccountReadinessStatus account={account} />
              </div>
            ))}
          </Space>
        </div>
      ) : null}

      {parsingSchedule && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>正在解析...</div>
        </div>
      )}

      {scheduleParseResult && !parsingSchedule && (
        <div style={{ marginTop: 16 }}>
          {scheduleParseResult.success ? (
            <div>
              <Alert
                message="解析成功"
                description="定时任务文本解析成功，请确认以下信息："
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
              {scheduleParseResult.data.schedule && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>定时规则：</div>
                  <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginBottom: 8 }}>
                    <div>
                      触发类型：
                      {scheduleParseResult.data.schedule.trigger_type === 'cron'
                        ? '定时任务 (Cron)'
                        : scheduleParseResult.data.schedule.trigger_type === 'interval'
                          ? '间隔任务 (Interval)'
                          : '单次任务'}
                    </div>
                    {scheduleParseResult.data.schedule.cron_expression && (
                      <div>Cron表达式：{scheduleParseResult.data.schedule.cron_expression}</div>
                    )}
                    {scheduleParseResult.data.schedule.run_at && (
                      <div>执行时间：{scheduleParseResult.data.schedule.run_at}</div>
                    )}
                    {scheduleParseResult.data.schedule.interval_seconds && (
                      <div>间隔（秒）：{scheduleParseResult.data.schedule.interval_seconds}</div>
                    )}
                    {(scheduleParseResult.data.schedule.start_at || scheduleParseResult.data.schedule.end_at) && (
                      <div>
                        区间：
                        {scheduleParseResult.data.schedule.start_at ? `起始 ${scheduleParseResult.data.schedule.start_at}` : '起始不限'}
                        {' ～ '}
                        {scheduleParseResult.data.schedule.end_at ? `结束 ${scheduleParseResult.data.schedule.end_at}` : '结束不限'}
                      </div>
                    )}
                    <div>时区：{scheduleParseResult.data.schedule.timezone || 'Asia/Shanghai'}</div>
                  </div>
                </div>
              )}

              {scheduleParseResult.data.next_runs_display && scheduleParseResult.data.next_runs_display.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>后续执行时间：</div>
                  <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                    {scheduleParseResult.data.next_runs_display.map((time, index) => (
                      <div key={index} style={{ marginBottom: 4 }}>
                        {index + 1}. {time}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scheduleParseResult.data.warnings && scheduleParseResult.data.warnings.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Alert
                    message="警告"
                    description={(
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {scheduleParseResult.data.warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    )}
                    type="warning"
                    showIcon
                  />
                </div>
              )}
            </div>
          ) : scheduleParseResult.needsClarification ? (
            <div>
              <Alert
                message="需要补充信息"
                description="请根据以下问题补充定时任务信息："
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              {scheduleParseResult.data.questions && scheduleParseResult.data.questions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <ul style={{ paddingLeft: 20 }}>
                    {scheduleParseResult.data.questions.map((question, index) => (
                      <li key={index}>{question}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <Alert
              message="解析失败"
              description={scheduleParseResult?.data?.message || scheduleParseResult?.error || '请调整输入后重试'}
              type="error"
              showIcon
            />
          )}
        </div>
      )}
    </Form>
  </Drawer>
);

export default TimedPublishScheduleDrawer;
